import React from 'react';

import {
  getMinerOutputType,
  getMinerOutputTicks,
  getMinerOutputIntervalSeconds,
} from './BeltItemSystem';
import { InventorySlotButton } from './components/InventorySlotButton';
import { ProgressBar } from './components/ProgressBar';
import { Sparkline } from './components/Sparkline';
import {
  Recipe,
  RECIPES,
  Building,
  BuildingType,
  ITEMS,
  RESEARCHES,
  RecipeIngredient,
  ItemType,
  ChestInventory,
  ITEM_TYPES,
  getItemStackSize,
  createEmptyItemCounts,
  BUILDINGS,
  BUILDINGS_BY_ID,
  BuildingItemRequirement,
} from './constants';
import { useUIState, useMapState } from './contexts/GameStateManager';
import { useToast } from './hooks/useToast';
import {
  getMinerSpeedMultiplier,
  getAssemblerSpeedMultiplier,
  getFactorySpeedMultiplier,
  getNextResearchRequirements,
  getResearchProgress,
  isBuildingUnlocked,
} from './ResearchSystem';
import { secondsToTicks, ticksToSeconds } from './utils/TickIntervals';
import { generateTerrainTile } from './WorldGeneration';

interface BuildingStatusMenuProps {
  building: Building;
  position: { x: number; y: number };
  onRecipeSelect: (recipe: Recipe | null) => void;
  selectedRecipe?: Recipe | null;
  inventoryCounts?: Record<string, number>;
  craftingProgress?: number;
  craftingStartTick?: number | null;
  minerTimerLayer?: Record<string, number>;
  minerOutputBufferLayer?: Record<string, number>;
  chestInventory?: ChestInventory | null;
  stackCapacity?: number;
}

export const BuildingStatusMenu: React.FC<BuildingStatusMenuProps> = ({
  building,
  position,
  onRecipeSelect,
  selectedRecipe,
  inventoryCounts = {},
  craftingProgress = 0,
  craftingStartTick = null,
  minerTimerLayer = {},
  minerOutputBufferLayer = {},
  chestInventory = null,
  stackCapacity,
}) => {
  // Use contexts for state management
  const uiState = useUIState();
  const mapState = useMapState();
  const { showSuccess, showError } = useToast();

  // Destructure values from contexts
  const { buildingStatusMenuTarget, closeBuildingStatusMenu: onClose } =
    uiState;
  const isOpen = buildingStatusMenuTarget !== null;
  const { gameMap, collectBuildingInventory } = mapState;

  if (!isOpen) return null;

  const effectiveStackCapacity = stackCapacity ?? 0;
  const isAssembler = building.id === BuildingType.ASSEMBLER;
  const isFactory = building.id === BuildingType.FACTORY;
  const isCraftingBuilding = isAssembler || isFactory;
  const isMiner = building.category === 'mining';
  const isChest = effectiveStackCapacity > 0;
  const isResearchFacility = building.id === BuildingType.RESEARCH_FACILITY;
  const isRocketSilo = building.id === BuildingType.ROCKET_SILO;
  const isPowerBuilding = building.category === 'power';
  const bufferCapacity = building.miningBufferCapacity ?? 0;
  const bufferKey = `${position.x},${position.y}`;
  const bufferedOutputCount = minerOutputBufferLayer[bufferKey] ?? 0;
  const outputAmountPerCycle = building.miningOutputPerCycle ?? 1;
  const minerSpeedMultiplier = getMinerSpeedMultiplier(mapState.researchState);
  const assemblerSpeedMultiplier = getAssemblerSpeedMultiplier(
    mapState.researchState
  );
  const factorySpeedMultiplier = getFactorySpeedMultiplier(
    mapState.researchState
  );

  const getEffectiveCraftingMultiplier = (): number => {
    if (building.id === BuildingType.FACTORY) {
      return factorySpeedMultiplier;
    }
    return assemblerSpeedMultiplier;
  };

  const getAdjustedCraftingSeconds = (recipe: Recipe): number => {
    const baseTicks = secondsToTicks(recipe.craftingTime);
    const adjustedMultiplier = Math.max(
      getEffectiveCraftingMultiplier() *
        Math.max(mapState.powerSatisfaction, 0.0001),
      0.0001
    );
    const adjustedTicks = Math.max(
      1,
      Math.round(baseTicks / adjustedMultiplier)
    );
    return ticksToSeconds(adjustedTicks);
  };

  if (
    !isCraftingBuilding &&
    !isMiner &&
    !isChest &&
    !isResearchFacility &&
    !isRocketSilo &&
    !isPowerBuilding
  ) {
    return null;
  }

  // For miners, get resource information
  let minerResourceType: ItemType | null = null;
  let minerResourceName = '';
  let minerResourceCharacter = '';
  let minerProgress = 0;
  let minerTimeRemaining: number | undefined;
  let minerReadyToOutput = false;

  if (isMiner && gameMap) {
    const key = `${position.x},${position.y}`;
    const terrainTile =
      key in gameMap
        ? gameMap[key]
        : generateTerrainTile(position.x, position.y);

    minerResourceType = getMinerOutputType(terrainTile, building);

    if (minerResourceType) {
      const resourceItem = Object.values(ITEMS).find(
        item => item.type === minerResourceType
      );
      minerResourceName = resourceItem?.name || minerResourceType;
      minerResourceCharacter = resourceItem?.character || '?';

      const ticksUntilOutput = getMinerOutputTicks(
        position,
        minerTimerLayer,
        minerSpeedMultiplier,
        building
      );
      const totalTicks = Math.max(
        1,
        secondsToTicks(
          getMinerOutputIntervalSeconds(minerSpeedMultiplier, building)
        )
      );
      minerProgress = Math.max(
        0,
        Math.min(1, (totalTicks - ticksUntilOutput) / totalTicks)
      );

      if (minerProgress > 0 && minerProgress < 1) {
        minerTimeRemaining = ticksUntilOutput / 60;
      }

      minerReadyToOutput =
        ticksUntilOutput <= 0 ||
        (bufferCapacity > 0 && bufferedOutputCount > 0);
    }
  }

  const recipes = Object.values(RECIPES).filter(recipe => {
    if (isAssembler) {
      return recipe.craftingBuilding === BuildingType.ASSEMBLER;
    }

    if (isFactory) {
      return recipe.craftingBuilding === BuildingType.FACTORY;
    }

    return false;
  });

  let headerSuffix = '';
  if (isCraftingBuilding) {
    headerSuffix = selectedRecipe
      ? ` - ${selectedRecipe.name}`
      : ' - Recipe Selection';
  } else if (isChest) {
    headerSuffix = ' - Storage';
  } else if (isResearchFacility) {
    headerSuffix = ' - Research Progress';
  } else if (isPowerBuilding) {
    headerSuffix = ' - Power Overview';
  }

  const chestItems: Record<ItemType, number> =
    chestInventory?.items ?? createEmptyItemCounts();
  const chestEntries = ITEM_TYPES.reduce(
    (
      acc: {
        totalStacks: number;
        items: {
          itemType: ItemType;
          count: number;
          stacks: number;
          name: string;
          character: string;
        }[];
      },
      itemType
    ) => {
      const count = chestItems[itemType] || 0;
      if (count <= 0) {
        return acc;
      }

      const stackSize = getItemStackSize(itemType);
      const stacks = Math.ceil(count / stackSize);
      acc.totalStacks += stacks;

      const itemConfig = Object.values(ITEMS).find(
        item => item.type === itemType
      );

      acc.items.push({
        itemType,
        count,
        stacks,
        name: itemConfig?.name || itemType,
        character: itemConfig?.character || '?',
      });

      return acc;
    },
    {
      totalStacks: 0,
      items: [] as {
        itemType: ItemType;
        count: number;
        stacks: number;
        name: string;
        character: string;
      }[],
    }
  );

  const assemblerStoredItems = ITEM_TYPES.reduce(
    (
      items: {
        itemType: ItemType;
        count: number;
        name: string;
        character: string;
      }[],
      itemType
    ) => {
      const count = inventoryCounts[itemType] ?? 0;
      if (count <= 0) {
        return items;
      }

      const itemConfig = Object.values(ITEMS).find(
        item => item.type === itemType
      );

      items.push({
        itemType,
        count,
        name: itemConfig?.name || itemType,
        character: itemConfig?.character || '?',
      });

      return items;
    },
    [] as {
      itemType: ItemType;
      count: number;
      name: string;
      character: string;
    }[]
  );

  const firstItemType = ITEM_TYPES[0];
  if (!firstItemType) {
    throw new Error('ITEM_TYPES must contain at least one entry');
  }
  const defaultStackSize = getItemStackSize(firstItemType);

  const formatSeconds = (seconds: number): string => {
    const clamped = Math.max(seconds, 0);
    const formatted = clamped.toFixed(1);
    return formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
  };

  const factoryDefinition = BUILDINGS.FACTORY;
  const assemblerDefinition = BUILDINGS.ASSEMBLER;
  const factoryUnlocked = isBuildingUnlocked(
    mapState.researchState,
    BuildingType.FACTORY
  );
  const canUpgradeAssembler = isAssembler && factoryUnlocked;
  const factoryCostItems = factoryDefinition.cost.items ?? [];
  const assemblerRefundItems = assemblerDefinition.cost.items ?? [];

  const formatCostItems = (
    items: readonly BuildingItemRequirement[]
  ): string => {
    if (!items || items.length === 0) {
      return 'None';
    }

    return items
      .map(requirement => {
        const itemConfig = Object.values(ITEMS).find(
          item => item.type === requirement.item
        );
        const itemName = itemConfig?.name ?? requirement.item;
        const character = itemConfig?.character ?? '?';
        return `${requirement.amount}x ${character} ${itemName}`;
      })
      .join(', ');
  };

  const upgradeCostLabel = formatCostItems(factoryCostItems);
  const upgradeRefundLabel = formatCostItems(assemblerRefundItems);

  const handleUpgradeAssembler = (): void => {
    const result = mapState.upgradeAssemblerToFactory(position);

    if (result.success) {
      showSuccess('Assembler upgraded to Factory. Assembler cost refunded.');
      return;
    }

    switch (result.reason) {
      case 'factory-locked':
        showError('Factory upgrades are locked by research.');
        break;
      case 'insufficient-items':
        showError('Not enough items available to upgrade to a Factory.');
        break;
      case 'not-assembler':
        showError('Only assemblers can be upgraded into factories.');
        break;
      default:
        showError('Unable to upgrade assembler right now.');
        break;
    }
  };

  const handleCollectItems = (targetItemType?: ItemType) => {
    const result = collectBuildingInventory(position, targetItemType);
    const entries = Object.entries(result.addedItems).filter(
      ([, count]) => (count ?? 0) > 0
    ) as Array<[ItemType, number]>;

    if (result.success && entries.length > 0) {
      const descriptions = entries.map(([type, count]) => {
        const itemConfig = Object.values(ITEMS).find(
          candidate => candidate.type === type
        );
        const itemName = itemConfig?.name ?? type;
        const character = itemConfig?.character ?? '?';
        return `${count}x ${character} ${itemName}`;
      });

      const baseMessage = `Collected ${descriptions.join(', ')}`;
      showSuccess(
        result.partial
          ? `${baseMessage} (inventory full for remaining items)`
          : baseMessage
      );
      return;
    }

    if (result.reason === 'inventory-full') {
      showError('Inventory is full.');
    } else if (result.reason === 'not-ready') {
      showError('Nothing ready to collect yet.');
    } else {
      showError('No items available to collect.');
    }
  };

  const renderMinerContent = () => {
    const minerIntervalSeconds = getMinerOutputIntervalSeconds(
      minerSpeedMultiplier,
      building
    );
    const displayInterval =
      minerIntervalSeconds >= 1
        ? `${minerIntervalSeconds.toFixed(1)}s`
        : `${Math.round(minerIntervalSeconds * 1000)}ms`;

    return (
      <div className='selected-recipe-view'>
        <div className='selected-recipe-details'>
          <div className='recipe-inputs'>
            <span className='label'>Resource:</span>
            {minerResourceType ? (
              <span className='ingredient'>
                {minerResourceCharacter} {minerResourceName}
              </span>
            ) : (
              <span className='ingredient no-resource'>
                No resource detected
              </span>
            )}
          </div>

          <div className='recipe-arrow'>
            →<div className='crafting-duration'>{displayInterval}</div>
          </div>

          <div className='recipe-outputs'>
            <span className='label'>Output:</span>
            {minerResourceType ? (
              <span className='ingredient'>
                {outputAmountPerCycle}x {minerResourceCharacter}{' '}
                {minerResourceName}
              </span>
            ) : (
              <span className='ingredient no-resource'>None</span>
            )}
          </div>
        </div>

        {minerResourceType && (
          <>
            <ProgressBar
              progress={minerProgress}
              label='Mining Progress:'
              timeRemaining={minerTimeRemaining}
              completedText='✓ Ready to output'
            />
            {bufferCapacity > 0 && (
              <div className='miner-buffer-status'>
                Buffered Output: {bufferedOutputCount} / {bufferCapacity}
              </div>
            )}
            <div className='inventory-actions'>
              <button
                className='collect-all-button'
                onClick={() => handleCollectItems()}
                type='button'
                disabled={!minerReadyToOutput}
                title={
                  minerReadyToOutput
                    ? 'Collect the mined resource.'
                    : bufferCapacity > 0
                      ? 'No buffered items available yet. Wait for the drill to finish a cycle.'
                      : 'Miner is still working. Wait for it to finish before collecting.'
                }
              >
                {minerReadyToOutput
                  ? 'Collect Output'
                  : 'Collect Output (Pending)'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderResearchFacilityContent = () => {
    const { researchState } = mapState;
    const selectedResearchId = researchState.selectedResearch;

    if (!selectedResearchId) {
      return (
        <div className='selected-recipe-view'>
          <div className='selected-recipe-details'>
            <span className='ingredient no-resource'>
              No active research selected.
            </span>
          </div>
        </div>
      );
    }

    const research = RESEARCHES[selectedResearchId];
    const requirements = getNextResearchRequirements(
      researchState,
      selectedResearchId
    );
    const totalRequired = requirements.reduce(
      (sum, requirement) => sum + requirement.amount,
      0
    );
    const rawProgress = getResearchProgress(researchState, selectedResearchId);
    const cappedProgress =
      totalRequired > 0 ? Math.min(rawProgress, totalRequired) : rawProgress;
    const progressValue =
      totalRequired > 0
        ? Math.min(cappedProgress / totalRequired, 1)
        : rawProgress > 0
          ? 1
          : 0;
    const isComplete =
      totalRequired > 0 ? cappedProgress >= totalRequired : rawProgress > 0;

    return (
      <div className='selected-recipe-view'>
        <div className='selected-recipe-details'>
          <div className='recipe-inputs'>
            <span className='label'>Active Research:</span>
            <span className='ingredient'>
              {research?.name ?? selectedResearchId}
            </span>
          </div>

          <div className='recipe-outputs'>
            <span className='label'>Requirements:</span>
            {requirements.length > 0 ? (
              requirements.map(requirement => {
                const itemConfig = Object.values(ITEMS).find(
                  item => item.type === requirement.item
                );
                const collected = Math.min(
                  getResearchProgress(
                    researchState,
                    selectedResearchId,
                    requirement.item
                  ),
                  requirement.amount
                );

                return (
                  <span
                    key={`${selectedResearchId}-${requirement.item}`}
                    className='ingredient'
                  >
                    {requirement.amount}x {itemConfig?.character || '?'}{' '}
                    {itemConfig?.name || requirement.item}
                    <span className='ingredient-count'>
                      ({collected}/{requirement.amount})
                    </span>
                  </span>
                );
              })
            ) : (
              <span className='ingredient no-resource'>
                No outstanding requirements
              </span>
            )}
          </div>
        </div>

        <ProgressBar
          progress={progressValue}
          label='Research Progress:'
          isComplete={isComplete}
          completedText='✓ Research complete'
        />
      </div>
    );
  };

  const renderChestContent = () => (
    <div className='chest-status-view'>
      <div className='chest-summary'>
        <span className='label'>Stacks Used:</span> {chestEntries.totalStacks} /{' '}
        {effectiveStackCapacity}
      </div>
      <div className='chest-stack-size'>
        <span className='label'>Stack Size:</span> {defaultStackSize}
      </div>
      {chestEntries.items.length > 0 ? (
        <>
          <div className='inventory-grid chest-inventory-grid'>
            {chestEntries.items.map((entry, index) => {
              const stackSize = getItemStackSize(entry.itemType);
              const progress = Math.min(entry.count / stackSize, 1);
              const title = `${entry.name} — ${entry.count} items (${entry.stacks} stack${
                entry.stacks !== 1 ? 's' : ''
              }). Click to collect.`;

              return (
                <div key={entry.itemType} className='chest-slot'>
                  <InventorySlotButton
                    className='filled chest-inventory-slot'
                    indexLabel={index + 1}
                    icon={entry.character}
                    name={entry.name}
                    countLabel={`${entry.count} / ${stackSize}`}
                    capacityLabel={`Stacks: ${entry.stacks}`}
                    typeLabel={entry.itemType}
                    progress={progress}
                    stackSquares={entry.stacks}
                    title={title}
                    ariaLabel={`${entry.name} stored in chest. ${entry.count} items across ${entry.stacks} stack${
                      entry.stacks !== 1 ? 's' : ''
                    }.`}
                    onClick={() => handleCollectItems(entry.itemType)}
                  />
                  <button
                    className='collect-item-button chest-collect-button'
                    onClick={() => handleCollectItems(entry.itemType)}
                    type='button'
                  >
                    Collect
                  </button>
                </div>
              );
            })}
          </div>
          <div className='inventory-actions chest-inventory-actions'>
            <span className='inventory-help-text'>
              Click a stack or use the buttons to collect items.
            </span>
            <button
              className='collect-all-button'
              onClick={() => handleCollectItems()}
              type='button'
            >
              Collect All
            </button>
          </div>
        </>
      ) : (
        <span className='ingredient no-resource'>Empty</span>
      )}
    </div>
  );

  const renderAssemblerStoredInventory = () => (
    <div className='stored-inventory-section'>
      <h4>Stored Items</h4>
      {assemblerStoredItems.length > 0 ? (
        <>
          <div className='stored-inventory-list'>
            {assemblerStoredItems.map(item => (
              <div key={item.itemType} className='inventory-item-row'>
                <span className='ingredient'>
                  {item.count}x {item.character} {item.name}
                </span>
                <button
                  className='collect-item-button'
                  onClick={() => handleCollectItems(item.itemType)}
                  type='button'
                >
                  Collect
                </button>
              </div>
            ))}
          </div>
          <div className='inventory-actions'>
            <button
              className='collect-all-button'
              onClick={() => handleCollectItems()}
              type='button'
            >
              Collect All
            </button>
          </div>
        </>
      ) : (
        <span className='ingredient no-resource'>No stored items</span>
      )}
    </div>
  );

  const renderPowerContent = () => {
    const buildingOutput = building.powerGeneration ?? 0;
    const totalProduction = mapState.powerProduction;
    const totalDemand = mapState.powerDemand;
    const satisfactionPercent = Math.round(
      Math.max(0, Math.min(1, mapState.powerSatisfaction)) * 100
    );
    const chartHistory = mapState.powerHistory.slice(-40);

    const chartWidth = 320;
    const chartHeight = 140;
    const hasHistory = chartHistory.length > 0;
    const demandEntries = Object.entries(
      mapState.powerDemandByBuildingType ?? {}
    )
      .filter(([, value]) => (value ?? 0) > 0)
      .map(([key, value]) => [key as BuildingType, value ?? 0] as const)
      .sort((a, b) => b[1] - a[1]);
    const demandHistoryByType = new Map<BuildingType, number[]>();
    demandEntries.forEach(([buildingType, currentDemand]) => {
      const historyValues = hasHistory
        ? chartHistory.map(
            entry => entry.demandByBuildingType?.[buildingType] ?? 0
          )
        : [currentDemand];
      demandHistoryByType.set(buildingType, historyValues);
    });

    const globalSparklineMax = demandEntries.reduce(
      (currentMax, [buildingType, currentDemand]) => {
        const historyValues = demandHistoryByType.get(buildingType) ?? [];
        const candidateValues =
          historyValues.length > 0 ? historyValues : [currentDemand];
        const localMax = Math.max(currentDemand, ...candidateValues);

        return Math.max(currentMax, localMax);
      },
      0
    );

    const getXForIndex = (index: number) => {
      if (chartHistory.length <= 1) {
        return chartWidth / 2;
      }

      return (index / (chartHistory.length - 1)) * chartWidth;
    };

    const powerValues = chartHistory.flatMap(entry => [
      entry.production,
      entry.demand,
    ]);
    const powerMin = powerValues.length > 0 ? Math.min(...powerValues, 0) : 0;
    const powerMax =
      powerValues.length > 0
        ? Math.max(...powerValues, totalProduction, totalDemand, 0)
        : Math.max(totalProduction, totalDemand, 0);
    const powerRange = powerMax - powerMin || 1;

    const getPowerY = (value: number) => {
      const clamped = Math.max(powerMin, Math.min(powerMax, value));
      const normalized = (clamped - powerMin) / powerRange;

      return chartHeight - normalized * chartHeight;
    };

    const productionPath = chartHistory
      .map((entry, index) => {
        const x = getXForIndex(index);
        const y = getPowerY(entry.production);

        return `${index === 0 ? 'M' : 'L'}${x},${y}`;
      })
      .join(' ');

    const demandPath = chartHistory
      .map((entry, index) => {
        const x = getXForIndex(index);
        const y = getPowerY(entry.demand);

        return `${index === 0 ? 'M' : 'L'}${x},${y}`;
      })
      .join(' ');

    const getSatisfactionY = (value: number) => {
      const normalized = Math.max(0, Math.min(1, value));

      return chartHeight - normalized * chartHeight;
    };

    const satisfactionPath = chartHistory
      .map((entry, index) => {
        const x = getXForIndex(index);
        const y = getSatisfactionY(entry.satisfaction);

        return `${index === 0 ? 'M' : 'L'}${x},${y}`;
      })
      .join(' ');

    const firstEntry = chartHistory[0];
    const lastEntry = chartHistory[chartHistory.length - 1];
    const lastIndex = chartHistory.length > 0 ? chartHistory.length - 1 : 0;
    const firstTick = firstEntry?.tick;
    const lastTick = lastEntry?.tick;
    const lastSatisfactionPercent = lastEntry
      ? Math.round(Math.max(0, Math.min(1, lastEntry.satisfaction)) * 100)
      : 0;

    const formatPower = (value: number): string => value.toFixed(1);

    return (
      <div className='power-status'>
        <div className='power-history'>
          <h4>Recent History</h4>
          {!hasHistory ? (
            <p>No power data recorded yet.</p>
          ) : (
            <div className='power-history-graph'>
              <svg
                className='power-history-svg'
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                role='img'
                aria-label='Power production and satisfaction over time'
                preserveAspectRatio='none'
              >
                <title>
                  Power production and satisfaction over recent ticks
                </title>
                {[0.25, 0.5, 0.75].map(ratio => (
                  <line
                    key={`grid-${ratio}`}
                    className='power-history-gridline'
                    x1={0}
                    y1={chartHeight * ratio}
                    x2={chartWidth}
                    y2={chartHeight * ratio}
                  />
                ))}
                <line
                  className='power-history-axis'
                  x1={0}
                  y1={chartHeight}
                  x2={chartWidth}
                  y2={chartHeight}
                />
                {productionPath && (
                  <path
                    className='power-history-line production'
                    d={productionPath}
                  />
                )}
                {demandPath && (
                  <path className='power-history-line demand' d={demandPath} />
                )}
                {satisfactionPath && (
                  <path
                    className='power-history-line satisfaction'
                    d={satisfactionPath}
                  />
                )}
                {lastEntry && (
                  <>
                    <circle
                      className='power-history-point production'
                      cx={getXForIndex(lastIndex)}
                      cy={getPowerY(lastEntry.production)}
                      r={3}
                    />
                    <circle
                      className='power-history-point demand'
                      cx={getXForIndex(lastIndex)}
                      cy={getPowerY(lastEntry.demand)}
                      r={3}
                    />
                    <circle
                      className='power-history-point satisfaction'
                      cx={getXForIndex(lastIndex)}
                      cy={getSatisfactionY(lastEntry.satisfaction)}
                      r={3}
                    />
                  </>
                )}
              </svg>
              <div className='power-history-legend'>
                <span className='legend-item'>
                  <span className='legend-swatch production' />
                  Production (MW)
                </span>
                <span className='legend-item'>
                  <span className='legend-swatch demand' />
                  Consumption (MW)
                </span>
                <span className='legend-item'>
                  <span className='legend-swatch satisfaction' />
                  Satisfaction (%)
                </span>
              </div>
              <div className='power-history-axis-labels'>
                <span>
                  Tick {firstTick ?? '?'} •{' '}
                  {formatPower(firstEntry?.production ?? 0)} MW
                </span>
                <span>
                  Tick {lastTick ?? '?'} •{' '}
                  {formatPower(lastEntry?.production ?? 0)} MW
                </span>
              </div>
              <div className='power-history-summary'>
                <span>
                  Latest Production: {formatPower(lastEntry?.production ?? 0)}{' '}
                  MW
                </span>
                <span>Latest Satisfaction: {lastSatisfactionPercent}%</span>
              </div>
            </div>
          )}
        </div>

        <div className='power-building-output'>
          <h4>Building Output</h4>
          <p>
            This {building.name} is producing {formatPower(buildingOutput)} MW
            of power.
          </p>
        </div>

        <div className='power-network-summary'>
          <h4>Global Network</h4>
          <table className='power-network-table'>
            <tbody>
              <tr>
                <th scope='row'>Total Production</th>
                <td className='power-stat-divider'>:</td>
                <td className='power-stat-value'>
                  {formatPower(totalProduction)} MW
                </td>
              </tr>
              <tr>
                <th scope='row'>Total Demand</th>
                <td className='power-stat-divider'>:</td>
                <td className='power-stat-value'>
                  {formatPower(totalDemand)} MW
                </td>
              </tr>
              <tr>
                <th scope='row'>Satisfaction</th>
                <td className='power-stat-divider'>:</td>
                <td className='power-stat-value'>{satisfactionPercent}%</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className='power-demand-breakdown'>
          <h4>Consumption by Building Type</h4>
          {demandEntries.length === 0 ? (
            <p className='power-demand-empty'>
              No buildings are consuming power right now.
            </p>
          ) : (
            <div className='power-demand-list'>
              {demandEntries.map(([buildingType, currentDemand]) => {
                const buildingDefinition = BUILDINGS_BY_ID[buildingType];
                const buildingName = buildingDefinition?.name ?? buildingType;
                const rawHistory = demandHistoryByType.get(buildingType) ?? [];
                const historyValues =
                  rawHistory.length > 0 ? rawHistory : [currentDemand];

                return (
                  <div className='power-demand-row' key={buildingType}>
                    <div className='power-demand-row-header'>
                      <span className='power-demand-name'>{buildingName}</span>
                      <span className='power-demand-value'>
                        {formatPower(currentDemand)} MW
                      </span>
                    </div>
                    <div className='power-demand-sparkline-wrapper'>
                      <Sparkline
                        className='power-demand-sparkline'
                        values={historyValues}
                        maxValue={globalSparklineMax}
                        strokeColor='#ff6a7a'
                        baselineColor='rgba(255, 106, 122, 0.35)'
                        ariaLabel={`Power consumption history for ${buildingName}`}
                      />
                      <div className='power-demand-sparkline-scale'>
                        <span>0</span>
                        <span>{formatPower(globalSparklineMax)} MW</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderUpgradeSection = () => {
    if (!canUpgradeAssembler) {
      return null;
    }

    return (
      <div className='assembler-upgrade-section'>
        <button
          className='upgrade-factory-button'
          onClick={handleUpgradeAssembler}
          type='button'
        >
          Upgrade to Factory
        </button>
        <div className='upgrade-factory-note'>
          <span>Cost: {upgradeCostLabel}</span>
          <span>Refund: {upgradeRefundLabel}</span>
        </div>
      </div>
    );
  };

  const renderAssemblerContent = () => {
    const upgradeSection = renderUpgradeSection();

    return !selectedRecipe ? (
      <>
        {upgradeSection}
        <div className='recipe-list'>
          <h4>Select a Recipe:</h4>
          {recipes.map(recipe => (
            <div
              key={recipe.id}
              className='recipe-item'
              onClick={() => onRecipeSelect(recipe)}
            >
              <div className='recipe-header'>
                <h4>{recipe.name}</h4>
                <span className='crafting-time'>
                  {formatSeconds(getAdjustedCraftingSeconds(recipe))}s
                </span>
              </div>

              <div className='recipe-details'>
                <div className='recipe-inputs'>
                  <span className='label'>Inputs:</span>
                  {recipe.inputs.map(
                    (input: RecipeIngredient, index: number) => {
                      const item =
                        ITEMS[
                          Object.keys(ITEMS).find(
                            key =>
                              ITEMS[key as keyof typeof ITEMS].type ===
                              input.item
                          ) as keyof typeof ITEMS
                        ];
                      const currentCount = inventoryCounts[input.item] || 0;
                      return (
                        <span key={index} className='ingredient'>
                          {input.count}x {item?.character || '?'}{' '}
                          {item?.name || input.item}
                          <span className='ingredient-count'>
                            ({currentCount})
                          </span>
                        </span>
                      );
                    }
                  )}
                </div>

                <div className='recipe-arrow'>→</div>

                <div className='recipe-outputs'>
                  <span className='label'>Outputs:</span>
                  {recipe.outputs.map(
                    (output: RecipeIngredient, index: number) => {
                      const item =
                        ITEMS[
                          Object.keys(ITEMS).find(
                            key =>
                              ITEMS[key as keyof typeof ITEMS].type ===
                              output.item
                          ) as keyof typeof ITEMS
                        ];
                      const currentCount = inventoryCounts[output.item] || 0;
                      return (
                        <span key={index} className='ingredient'>
                          {output.count}x {item?.character || '?'}{' '}
                          {item?.name || output.item}
                          <span className='ingredient-count'>
                            ({currentCount})
                          </span>
                        </span>
                      );
                    }
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    ) : (
      <>
        {upgradeSection}
        <div className='selected-recipe-view'>
          <div className='selected-recipe-details'>
            <div className='recipe-inputs'>
              <span className='label'>Inputs:</span>
              {selectedRecipe.inputs.map(
                (input: RecipeIngredient, index: number) => {
                  const item =
                    ITEMS[
                      Object.keys(ITEMS).find(
                        key =>
                          ITEMS[key as keyof typeof ITEMS].type === input.item
                      ) as keyof typeof ITEMS
                    ];
                  const currentCount = inventoryCounts[input.item] || 0;
                  return (
                    <span key={index} className='ingredient'>
                      {input.count}x {item?.character || '?'}{' '}
                      {item?.name || input.item}
                      <span className='ingredient-count'>
                        ({currentCount}/{input.count})
                      </span>
                    </span>
                  );
                }
              )}
            </div>

            <div className='recipe-arrow'>
              →
              <div className='crafting-duration'>
                {formatSeconds(getAdjustedCraftingSeconds(selectedRecipe))}s
              </div>
            </div>

            <div className='recipe-outputs'>
              <span className='label'>Outputs:</span>
              {selectedRecipe.outputs.map(
                (output: RecipeIngredient, index: number) => {
                  const item =
                    ITEMS[
                      Object.keys(ITEMS).find(
                        key =>
                          ITEMS[key as keyof typeof ITEMS].type === output.item
                      ) as keyof typeof ITEMS
                    ];
                  const currentCount = inventoryCounts[output.item] || 0;
                  return (
                    <span key={index} className='ingredient'>
                      {output.count}x {item?.character || '?'}{' '}
                      {item?.name || output.item}
                      <span className='ingredient-count'>({currentCount})</span>
                    </span>
                  );
                }
              )}
            </div>
          </div>

          <div className='inventory-status'>
            {selectedRecipe.inputs.every(
              input => (inventoryCounts[input.item] || 0) >= input.count
            ) ? (
              <span className='inventory-ready'>
                ✅ Ready to craft automatically
              </span>
            ) : (
              <span className='inventory-missing'>
                ⚠️ Waiting for required inputs
              </span>
            )}
          </div>

          <div className='crafting-progress'>
            <ProgressBar
              progress={craftingProgress}
              label='Crafting Progress:'
              showReadyWaiting={craftingStartTick === null}
              completedText='✓ Crafting complete'
            />
          </div>

          <div className='recipe-actions'>
            <button
              className='clear-recipe-button'
              onClick={() => onRecipeSelect(null)}
            >
              Clear Recipe
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className='building-status-menu-overlay'>
      <div className='building-status-menu'>
        <div className='building-status-menu-header'>
          <h3>{`${building.name}${headerSuffix}`}</h3>
          <button
            className='close-button'
            onClick={onClose}
            title='Close menu (ESC)'
          >
            ✕
          </button>
        </div>

        <div className='building-status-menu-content'>
          {isRocketSilo ? (
            <div className='launch-rocket-section'>
              <p className='launch-rocket-warning'>
                Launching a rocket will reset your entire factory but grants +1
                prestige point.
              </p>
              <p className='launch-rocket-progress'>
                Current prestige: {mapState.prestigePoints} • Total launches:{' '}
                {mapState.prestigeCount}
              </p>
              <button
                className='launch-rocket-button'
                onClick={() => {
                  mapState.launchRocket();
                  onClose();
                  showSuccess('Rocket launched! Prestige +1');
                }}
              >
                Launch Rocket
              </button>
            </div>
          ) : isMiner ? (
            renderMinerContent()
          ) : isChest ? (
            renderChestContent()
          ) : isResearchFacility ? (
            renderResearchFacilityContent()
          ) : isPowerBuilding ? (
            renderPowerContent()
          ) : (
            <>
              {renderAssemblerContent()}
              {renderAssemblerStoredInventory()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
